import { useEffect, useState } from "react";

/**
 * True only once `active` has stayed true for `delayMs` without interruption.
 *
 * Every loading affordance in this app is gated through this. A spinner that
 * appears for 80ms and vanishes is worse than no spinner: it reads as a glitch,
 * and on a fast connection that is what most requests look like. Waiting a beat
 * before showing anything means an indicator only ever appears when there is
 * genuinely something to wait for.
 *
 * Falling edge is deliberately immediate — the moment the data lands, the
 * indicator goes, with no minimum display time. A bar that lingers after the
 * content is already on screen is just lying about what the app is doing.
 */
export function useDelayedFlag(active: boolean, delayMs: number): boolean {
  const [elapsed, setElapsed] = useState(false);

  useEffect(() => {
    if (!active) {
      setElapsed(false);
      return;
    }
    const timer = setTimeout(() => setElapsed(true), delayMs);
    return () => clearTimeout(timer);
  }, [active, delayMs]);

  return active && elapsed;
}

/** A request slow enough to be worth a progress bar. */
export const BAR_DELAY_MS = 400;

/**
 * Long enough that the user has started wondering whether it is broken.
 * A cold Worker plus a cold Hyperdrive connection can genuinely take this
 * long, so the honest answer is "still going", not an error.
 */
export const SLOW_DELAY_MS = 2500;
